/* wallPolygons.js — WALL POLYGON PIPELINE (wall-engine mandate, Stage W1).
 *
 * centerline → offset left/right by the wall's OWN half-thickness → END-TRIMMED
 * by the owning nodes' join geometry (wallJoins fan) → closed polygon. Geometry
 * is generated FROM the centerline — never simple rectangles glued at corners.
 * This is the render/cleanup API W3/W2 consume; W1 keeps it engine-pure.
 *
 * getWallPolygons(walls) → [{ wallId, part, polygon, faces, joins, thickness }]
 *   polygon   [[x,z],…] CCW, closed ring (last→first edge implied). Joined
 *             walls' polygons contain the SAME wedge-vertex float64 arrays the
 *             join solver stored on both ends — exact shared vertices, no gap,
 *             no recomputation (assert with toBe).
 *   faces     { left:[[x,z],[x,z]], right:[…], centerline:[…] } — the trimmed
 *             face spans, endpoints taken from the polygon's own cap points
 *             (they lie ON the face lines even at bevel/step corners).
 *   joins     { start: nodeId|null, end: nodeId|null } — null only at a
 *             through-crossing cut (no graph node exists there).
 *   part      0 for whole walls; a wall crossed mid-mid by a longer host
 *             (segmentCrossings) is emitted as parts 0..k butting the host's
 *             faces — two T-butts per crossing, host polygon continuous, no
 *             duplicate polygon in the crossing square (HOST OWNS IT).
 *   Output sorted by (wallId, part); deterministic under input permutation.
 *
 * TEES: the host polygon stays ONE unbroken rectangle (host face wins — the
 * branch cap already sits ON the host's near face, see wallJoins). The graph's
 * derived T-splits do NOT split the emitted host polygon.
 *
 * ARCS (W1: joined ends at 2-wall nodes): banded polygon per the arcBandGeometry
 * radii discipline (outR/inR = r ± eff/2, arcBandPreview sampling density) with
 * end caps REPLACED by the node-trim seam caps computed by wallJoins (closed-form
 * line-circle intersections). Radial-cap fallback (short arc / near-full sweep /
 * tee-cross node) is carried through from joins[end].fallback — pinned, not
 * silent. Ring endpoint samples are the cap points THEMSELVES (no re-derivation).
 *
 * getJoinPatches(walls) → [] — RESERVED. Fan-solved joins need no junction
 * infill polygons; the API exists so W3's contract never changes shape if a
 * future case (e.g. curved-wall booleans behind geomClip.js) needs patches.
 *
 * MEMO: single-slot content fingerprint — the SAME key as getWallJoins
 * (joinFingerprint: graph key + per-wall thickness + live WALL_THICKNESS + arc
 * params), getWallGraph pattern (wallGraph.js:62-74).
 */
import { getWallGraph } from "./wallGraph";
import { arcSweep } from "./walls";
import {
  getWallJoins, segmentCrossings, effectiveThickness, joinFingerprint, EPS_CROSS,
} from "./wallJoins";

const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const scale = (a, s) => [a[0] * s, a[1] * s];
const len2 = (a) => Math.hypot(a[0], a[1]);
const unit = (a) => { const l = len2(a) || 1; return [a[0] / l, a[1] / l]; };
const leftPerp = (d) => [-d[1], d[0]];
const cross2 = (a, b) => a[0] * b[1] - a[1] * b[0];
const dot2 = (a, b) => a[0] * b[0] + a[1] * b[1];
const mid2 = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

const isArcWall = (w) =>
  !!w && w.type === "arc" && Array.isArray(w.center) && typeof w.radius === "number" && w.radius > 0;

function signedArea(poly) {
  let s = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++)
    s += poly[j][0] * poly[i][1] - poly[i][0] * poly[j][1];
  return s / 2;
}

/** Infinite-line intersection p1+t·d1 ∩ p2+s·d2 (caller guarantees non-parallel). */
function lineX(p1, d1, p2, d2) {
  const den = cross2(d1, d2);
  const t = cross2(sub(p2, p1), d2) / den;
  return add(p1, scale(d1, t));
}

let _memo = { key: null, val: null };

export function getWallPolygons(walls) {
  const key = "polys|" + joinFingerprint(walls);
  if (_memo.key === key) return _memo.val;
  const val = buildWallPolygons(walls || []);
  _memo = { key, val };
  return val;
}

/** RESERVED (see header): fan-solved joins need no infill; stable API for W3. */
export function getJoinPatches(walls) {
  return [];
}

const findEnd = (join, wallId, end) =>
  join ? join.ends.find((e) => e.wallId === wallId && e.end === end) || null : null;

function buildWallPolygons(walls) {
  const graph = getWallGraph(walls);
  const joins = getWallJoins(walls);
  const crossings = segmentCrossings(walls);
  const byId = new Map();
  walls.forEach((w) => { if (w && graph.walls.has(w.id)) byId.set(w.id, w); });
  const out = [];

  byId.forEach((w, wid) => {
    const info = graph.walls.get(wid);
    const eff = effectiveThickness(w);
    if (isArcWall(w)) {
      const rec = buildArcPolygon(w, info, joins, eff);
      if (rec) out.push(rec);
      return;
    }
    const u = info.dir;
    const left = leftPerp(u);
    const h = eff / 2;
    const startEntry = findEnd(joins.get(info.startNode), wid, "start");
    const endEntry = findEnd(joins.get(info.endNode), wid, "end");
    // caps in CCW-splice order: startCap = wallLeft→wallRight, endCap = wallRight→wallLeft
    const startCap = startEntry ? startEntry.cap
      : [add(w.start, scale(left, h)), add(w.start, scale(left, -h))];
    const endCap = endEntry ? endEntry.cap
      : [add(w.end, scale(left, -h)), add(w.end, scale(left, h))];

    // Through-crossing cuts (this wall is the OTHER/minor wall): square butts
    // against the host's face lines — closed form, host polygon untouched.
    const cuts = crossings.filter((c) => c.otherId === wid).sort((a, b) => a.tOther - b.tOther);
    const capsSeq = [{ cap: startCap, nodeId: info.startNode }];
    const leftLine = { p: add(w.start, scale(left, h)), d: u };
    const rightLine = { p: add(w.start, scale(left, -h)), d: u };
    let usable = true;
    const cutPairs = [];
    for (const c of cuts) {
      const host = byId.get(c.hostId);
      if (!host) continue;
      const uH = unit(sub(host.end, host.start));
      const perpH = leftPerp(uH);
      const hH = effectiveThickness(host) / 2;
      const sinD = dot2(perpH, u);
      if (Math.abs(sinD) < EPS_CROSS) { usable = false; break; }
      const sigma = sinD > 0 ? -1 : 1; // near-face sign for the lower-t part
      const nearPt = add(c.p, scale(perpH, sigma * hH));
      const farPt = add(c.p, scale(perpH, -sigma * hH));
      cutPairs.push({
        endCap: [lineX(rightLine.p, rightLine.d, nearPt, uH), lineX(leftLine.p, leftLine.d, nearPt, uH)],
        startCap: [lineX(leftLine.p, leftLine.d, farPt, uH), lineX(rightLine.p, rightLine.d, farPt, uH)],
      });
    }
    if (!usable) cutPairs.length = 0;
    cutPairs.forEach((cp) => {
      capsSeq[capsSeq.length - 1].next = { cap: cp.endCap, nodeId: null };
      capsSeq.push({ cap: cp.startCap, nodeId: null });
    });
    capsSeq[capsSeq.length - 1].next = { cap: endCap, nodeId: info.endNode };

    let part = 0;
    capsSeq.forEach((seg) => {
      const sCap = seg.cap, eCap = seg.next.cap;
      const sMid = mid2(sCap[0], sCap[sCap.length - 1]);
      const eMid = mid2(eCap[0], eCap[eCap.length - 1]);
      if (dot2(sub(eMid, sMid), u) <= 1e-9) return; // degenerate sliver (cuts too close) — skip
      const polygon = [...sCap, ...eCap];
      out.push({
        wallId: wid,
        part: part++,
        polygon,
        faces: {
          left: [sCap[0], eCap[eCap.length - 1]],
          right: [sCap[sCap.length - 1], eCap[0]],
          centerline: [sMid, eMid],
        },
        joins: { start: seg.nodeId, end: seg.next.nodeId },
        thickness: eff,
      });
    });
  });

  // Determinism + stable consumer contract: sort by (wallId, part);
  // normalize any CW ring (CW arc sweeps) to CCW.
  out.forEach((rec) => { if (signedArea(rec.polygon) < 0) rec.polygon.reverse(); });
  out.sort((a, b) =>
    a.wallId < b.wallId ? -1 : a.wallId > b.wallId ? 1 : a.part - b.part);
  return out;
}

function buildArcPolygon(w, info, joins, eff) {
  const c = w.center;
  const sweep = arcSweep(w);
  if (!isFinite(sweep) || Math.abs(sweep) < 1e-6) return null;
  const angA = Math.atan2(w.start[1] - c[1], w.start[0] - c[0]);
  const h = eff / 2;
  const outR = w.radius + h, inR = Math.max(0.001, w.radius - h); // arcBandGeometry radii
  const at = (rad, ang) => [c[0] + rad * Math.cos(ang), c[1] + rad * Math.sin(ang)];
  const sE = findEnd(joins.get(info.startNode), w.id, "start");
  const eE = findEnd(joins.get(info.endNode), w.id, "end");
  const capS = (sE && sE.arcCap) || { outer: at(outR, angA), inner: at(inR, angA), tOuter: 0, tInner: 0 };
  const capE = (eE && eE.arcCap) || { outer: at(outR, angA + sweep), inner: at(inR, angA + sweep), tOuter: 1, tInner: 1 };
  const N = Math.max(2, Math.ceil(Math.abs(sweep) / (Math.PI / 32))); // arcBandPreview density
  const outer = [], inner = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    outer.push(at(outR, angA + sweep * (capS.tOuter + (capE.tOuter - capS.tOuter) * f)));
    inner.push(at(inR, angA + sweep * (capS.tInner + (capE.tInner - capS.tInner) * f)));
  }
  outer[0] = capS.outer; outer[N] = capE.outer; // caps verbatim — no re-derivation drift
  inner[0] = capS.inner; inner[N] = capE.inner;
  const polygon = outer.concat(inner.slice().reverse());
  // left of travel: CCW sweep ⇒ leftPerp(tangent) points toward the center (inner ring)
  const leftRing = sweep >= 0 ? inner : outer;
  const rightRing = sweep >= 0 ? outer : inner;
  return {
    wallId: w.id,
    part: 0,
    polygon,
    faces: {
      left: [leftRing[0], leftRing[N]],
      right: [rightRing[0], rightRing[N]],
      centerline: [[w.start[0], w.start[1]], [w.end[0], w.end[1]]],
    },
    joins: { start: info.startNode, end: info.endNode },
    thickness: eff,
  };
}
